import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertCircle,
  ArrowDown,
  Edit,
  EyeOff,
  Loader2,
  MessageSquare,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  X,
} from "lucide-react";
import type { Step } from "@/lib/api/sms-lifecycle";
import { useCan } from "@/lib/casl/use-can";
import {
  useAddStep,
  useCohorts,
  useDeleteStep,
  useSettings,
  useSteps,
  useUpdateStep,
} from "@/lib/hooks/sms-lifecycle/use-sms-lifecycle";

const SMS_MAX_CHARS = 320;
const SEND_WINDOW_MAX = "23:00";
const DEFAULT_GLOBAL_SEND_TIME = "15:00";

interface StepsTabProps {
  cohortId: string;
  onBack: () => void;
}

export const StepsTab = ({ cohortId, onBack }: StepsTabProps) => {
  const can = useCan();
  const canCreate = can("create", "sms-lifecycle");
  const canUpdate = can("update", "sms-lifecycle");
  const canDelete = can("delete", "sms-lifecycle");

  const { data: cohorts } = useCohorts();
  const cohort = cohorts?.find((c) => c.id === cohortId);
  const { data: steps, isLoading } = useSteps(cohortId);
  const { data: settings } = useSettings();
  const addMutation = useAddStep(cohortId);
  const updateMutation = useUpdateStep(cohortId);
  const deleteMutation = useDeleteStep(cohortId);

  const globalSendTime =
    settings?.scheduler_send_time ?? DEFAULT_GLOBAL_SEND_TIME;
  const isValidWindow = (start: string, end: string) =>
    start >= globalSendTime && end <= SEND_WINDOW_MAX && start < end;

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newMessages, setNewMessages] = useState<string[]>([""]);
  const [newStartTime, setNewStartTime] = useState("17:15");
  const [newEndTime, setNewEndTime] = useState("20:45");
  const [newGapHours, setNewGapHours] = useState(36);
  const [newInsertAfter, setNewInsertAfter] = useState<string>("__tail__");

  const availableParents = steps?.filter((s) => !s.childId) ?? [];

  const [editingStep, setEditingStep] = useState<Step | null>(null);
  const [editMessages, setEditMessages] = useState<string[]>([""]);
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editGapHours, setEditGapHours] = useState(36);

  const handleAddStep = async () => {
    const filtered = newMessages.filter((m) => m.trim());
    if (filtered.length === 0) return;
    await addMutation.mutateAsync({
      messages: filtered,
      sendStartTime: newStartTime,
      sendEndTime: newEndTime,
      minGapHours: newGapHours,
      insertAfterStepId:
        newInsertAfter === "__head__"
          ? null
          : newInsertAfter === "__tail__"
            ? undefined
            : newInsertAfter,
    });
    setShowAddDialog(false);
    setNewMessages([""]);
    setNewStartTime("17:15");
    setNewEndTime("20:45");
    setNewGapHours(36);
    setNewInsertAfter("__tail__");
  };

  const openEditDialog = (step: Step) => {
    setEditingStep(step);
    setEditMessages([...step.messages]);
    setEditStartTime(step.sendStartTime.slice(0, 5));
    setEditEndTime(step.sendEndTime.slice(0, 5));
    setEditGapHours(step.minGapHours);
  };

  const handleSaveEdit = async () => {
    if (!editingStep) return;
    const filtered = editMessages.filter((m) => m.trim());
    if (filtered.length === 0) return;
    await updateMutation.mutateAsync({
      id: editingStep.id,
      messages: filtered,
      sendStartTime: editStartTime,
      sendEndTime: editEndTime,
      minGapHours: editGapHours,
    });
    setEditingStep(null);
  };

  const handleToggleEnabled = async (step: Step) => {
    await updateMutation.mutateAsync({ id: step.id, enabled: !step.enabled });
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  if (isLoading)
    return (
      <div className="flex h-24 w-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="outline" onClick={onBack}>
            ← Back to Cohorts
          </Button>
          <h2 className="mt-2 text-2xl font-bold">{cohort?.name}</h2>
          <p className="text-muted-foreground">{cohort?.description}</p>
        </div>
        {canCreate && (
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Step
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add Step</DialogTitle>
                <DialogDescription>
                  Choose where to insert the new step in the sequence.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {steps && steps.length > 0 && (
                  <div>
                    <Label>Insert After</Label>
                    <Select
                      value={newInsertAfter}
                      onValueChange={setNewInsertAfter}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__head__">
                          Beginning (new root)
                        </SelectItem>
                        {availableParents.map((s) => {
                          const idx = steps.findIndex((st) => st.id === s.id);
                          return (
                            <SelectItem key={s.id} value={s.id}>
                              After Step {idx + 1}
                              {s.messages[0]
                                ? ` — ${s.messages[0].slice(0, 40)}${s.messages[0].length > 40 ? "…" : ""}`
                                : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Only steps without an existing child are available as
                      parents.
                    </p>
                  </div>
                )}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Message Variants</Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        One variant is chosen at random per send. Add multiple
                        for A/B testing.
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-xs tabular-nums">
                      {newMessages.length}{" "}
                      {newMessages.length === 1 ? "variant" : "variants"}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {newMessages.map((msg, i) => (
                      <div
                        key={i}
                        className="group relative rounded-lg border bg-card p-3"
                      >
                        <div className="mb-1.5 flex items-center gap-2">
                          <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground">
                            Variant {String.fromCharCode(65 + i)}
                          </span>
                          {newMessages.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="ml-auto h-6 w-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                              onClick={() =>
                                setNewMessages(
                                  newMessages.filter((_, j) => j !== i),
                                )
                              }
                            >
                              <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                            </Button>
                          )}
                        </div>
                        <Textarea
                          value={msg}
                          onChange={(e) => {
                            const updated = [...newMessages];
                            updated[i] = e.target.value.slice(0, SMS_MAX_CHARS);
                            setNewMessages(updated);
                          }}
                          maxLength={SMS_MAX_CHARS}
                          placeholder="Message text (use %firstname%, %mentorname% for personalization)"
                          rows={4}
                          className="min-h-[80px] resize-y rounded-none !border-none !bg-transparent !p-0 !shadow-none !outline-none !ring-0"
                        />
                        <div
                          className={`mt-1 text-right text-[10px] ${msg.length > SMS_MAX_CHARS * 0.9 ? "text-destructive" : "text-muted-foreground"}`}
                        >
                          {msg.length}/{SMS_MAX_CHARS}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-dashed"
                    onClick={() => setNewMessages([...newMessages, ""])}
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Add Variant
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Send Start Time</Label>
                    <Input
                      type="time"
                      min={globalSendTime}
                      max={SEND_WINDOW_MAX}
                      value={newStartTime}
                      onChange={(e) => setNewStartTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Send End Time</Label>
                    <Input
                      type="time"
                      min={globalSendTime}
                      max={SEND_WINDOW_MAX}
                      value={newEndTime}
                      onChange={(e) => setNewEndTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Min Gap (hours)</Label>
                    <Input
                      type="number"
                      value={newGapHours}
                      onChange={(e) => setNewGapHours(Number(e.target.value))}
                    />
                  </div>
                </div>
                {!isValidWindow(newStartTime, newEndTime) && (
                  <p className="text-xs text-destructive">
                    Send window must be between {globalSendTime} and{" "}
                    {SEND_WINDOW_MAX} EST, and start must be before end.
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddStep}
                  disabled={
                    addMutation.isPending ||
                    !isValidWindow(newStartTime, newEndTime)
                  }
                >
                  {addMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Add Step
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Message Steps</CardTitle>
          <CardDescription>
            Steps are sent in sequence. Disabled steps are skipped — the chain
            flows past them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {steps && steps.length > 0 && steps.some((s) => !s.enabled) && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm dark:border-yellow-900 dark:bg-yellow-950">
              <EyeOff className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
              <div>
                <span className="font-medium text-yellow-800 dark:text-yellow-200">
                  Active flow:{" "}
                </span>
                <span className="text-yellow-700 dark:text-yellow-300">
                  {(() => {
                    const flow = steps
                      .map((s, i) => ({
                        label: `Step ${i + 1}`,
                        enabled: s.enabled,
                      }))
                      .filter((s) => s.enabled)
                      .map((s) => s.label)
                      .join(" → ");
                    if (!flow) return "No active steps";
                    const lastStep = steps[steps.length - 1];
                    if (lastStep?.childId) {
                      const targetIndex = steps.findIndex(
                        (s) => s.id === lastStep.childId,
                      );
                      if (targetIndex !== -1)
                        return `${flow} → ↩ Step ${targetIndex + 1}`;
                    }
                    return flow;
                  })()}
                </span>
              </div>
            </div>
          )}
          <div>
            {steps?.map((step, index) => (
              <div key={step.id}>
                {index > 0 && (
                  <div className="flex justify-center py-1">
                    <ArrowDown
                      className={`h-4 w-4 ${step.enabled && steps[index - 1]?.enabled ? "text-muted-foreground" : "text-muted-foreground/40"}`}
                    />
                  </div>
                )}
                <Card
                  className={
                    !step.enabled
                      ? "border-dashed border-muted-foreground/30"
                      : ""
                  }
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="mb-2 flex items-center gap-2">
                          <Badge
                            variant={step.isRoot ? "default" : "secondary"}
                            className={!step.enabled ? "opacity-50" : ""}
                          >
                            Step {index + 1} {step.isRoot && "(Root)"}
                          </Badge>
                          {!step.enabled && (
                            <Badge
                              variant="outline"
                              className="border-yellow-300 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-400"
                            >
                              <EyeOff className="mr-1 h-3 w-3" />
                              Skipped
                            </Badge>
                          )}
                          <Switch
                            checked={step.enabled}
                            onCheckedChange={() => handleToggleEnabled(step)}
                            disabled={
                              !canUpdate ||
                              (updateMutation.isPending &&
                                updateMutation.variables?.id === step.id)
                            }
                            aria-label={`Toggle step ${index + 1} enabled`}
                          />
                        </div>
                        <div
                          className={`space-y-1.5 text-sm ${!step.enabled ? "opacity-50" : ""}`}
                        >
                          {step.messages.map((msg, i) => (
                            <div
                              key={i}
                              className="group/variant flex items-start gap-2 rounded bg-muted p-2"
                            >
                              <span className="mt-0.5 shrink-0 rounded bg-background px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                                {String.fromCharCode(65 + i)}
                              </span>
                              <span className="flex-1 text-sm">{msg}</span>
                              {step.messages.length > 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 shrink-0 p-0 opacity-0 transition-opacity group-hover/variant:opacity-100"
                                  onClick={() =>
                                    updateMutation.mutate({
                                      id: step.id,
                                      messages: step.messages.filter(
                                        (_, j) => j !== i,
                                      ),
                                    })
                                  }
                                  disabled={
                                    !canUpdate ||
                                    (updateMutation.isPending &&
                                      updateMutation.variables?.id === step.id)
                                  }
                                >
                                  <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                        <div
                          className={`mt-2 flex items-center gap-2 ${!step.enabled ? "opacity-50" : ""}`}
                        >
                          <div className="text-xs text-muted-foreground">
                            Send window: {step.sendStartTime} -{" "}
                            {step.sendEndTime} | Min gap: {step.minGapHours}h
                          </div>
                          <Badge variant="outline" className="text-xs">
                            Node-Specific Timing
                          </Badge>
                        </div>
                        <p
                          className={`mt-1 text-xs text-muted-foreground ${!step.enabled ? "opacity-50" : ""}`}
                        >
                          Timing hierarchy: Node (highest) → Cohort → Global (3
                          PM default)
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(step)}
                          disabled={!canUpdate}
                          aria-label={`Edit step ${index + 1}`}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(step.id)}
                          disabled={
                            !canDelete ||
                            (deleteMutation.isPending &&
                              deleteMutation.variables === step.id)
                          }
                          aria-label={`Delete step ${index + 1}`}
                        >
                          {deleteMutation.isPending &&
                          deleteMutation.variables === step.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3 text-destructive" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
            {(() => {
              if (!steps || steps.length < 2) return null;
              const lastStep = steps[steps.length - 1];
              if (!lastStep.childId) return null;
              const targetIndex = steps.findIndex(
                (s) => s.id === lastStep.childId,
              );
              if (targetIndex === -1) return null;
              return (
                <div className="flex items-center justify-center gap-2 py-2">
                  <div className="flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-400">
                    <RotateCcw className="h-3.5 w-3.5" />
                    Loops back to Step {targetIndex + 1}
                  </div>
                </div>
              );
            })()}
            {steps?.length === 0 && (
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 p-4 text-sm text-muted-foreground">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  No steps defined. Add your first step to get started.
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {canUpdate && editingStep && (
        <Dialog open={!!editingStep} onOpenChange={() => setEditingStep(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Step</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Message Variants</Label>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      One variant is chosen at random per send.
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs tabular-nums">
                    {editMessages.length}{" "}
                    {editMessages.length === 1 ? "variant" : "variants"}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {editMessages.map((msg, i) => (
                    <div
                      key={i}
                      className="group relative rounded-lg border bg-card p-3"
                    >
                      <div className="mb-1.5 flex items-center gap-2">
                        <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground">
                          Variant {String.fromCharCode(65 + i)}
                        </span>
                        {editMessages.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-auto h-6 w-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                            onClick={() =>
                              setEditMessages(
                                editMessages.filter((_, j) => j !== i),
                              )
                            }
                          >
                            <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                          </Button>
                        )}
                      </div>
                      <Textarea
                        value={msg}
                        onChange={(e) => {
                          const updated = [...editMessages];
                          updated[i] = e.target.value.slice(0, SMS_MAX_CHARS);
                          setEditMessages(updated);
                        }}
                        maxLength={SMS_MAX_CHARS}
                        placeholder="Message text (use %firstname%, %mentorname% for personalization)"
                        rows={4}
                        className="min-h-[80px] resize-y rounded-none !border-none !bg-transparent !p-0 !shadow-none !outline-none !ring-0"
                      />
                      <div
                        className={`mt-1 text-right text-[10px] ${msg.length > SMS_MAX_CHARS * 0.9 ? "text-destructive" : "text-muted-foreground"}`}
                      >
                        {msg.length}/{SMS_MAX_CHARS}
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-dashed"
                  onClick={() => setEditMessages([...editMessages, ""])}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Add Variant
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Send Start Time</Label>
                  <Input
                    type="time"
                    min={globalSendTime}
                    max={SEND_WINDOW_MAX}
                    value={editStartTime}
                    onChange={(e) => setEditStartTime(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Send End Time</Label>
                  <Input
                    type="time"
                    min={globalSendTime}
                    max={SEND_WINDOW_MAX}
                    value={editEndTime}
                    onChange={(e) => setEditEndTime(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Min Gap (hours)</Label>
                  <Input
                    type="number"
                    value={editGapHours}
                    onChange={(e) => setEditGapHours(Number(e.target.value))}
                  />
                </div>
              </div>
              {!isValidWindow(editStartTime, editEndTime) && (
                <p className="text-xs text-destructive">
                  Send window must be between {globalSendTime} and{" "}
                  {SEND_WINDOW_MAX} EST, and start must be before end.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingStep(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={
                  updateMutation.isPending ||
                  !isValidWindow(editStartTime, editEndTime)
                }
              >
                {updateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
